import React, { useContext, useEffect, useId, useState } from 'react';

import { I18nContext } from '../context/translations/TranslationsContext.context';
import { Switch } from '../components/ui/switch';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle
} from '../components/ui/alert-dialog';
import { clearContextAndLocalStorage } from '../utils/clearLocalStorageAndContext';
import { borrarAcceso } from './accesoGuardado';
import { TipoDeBiometria, tipoDeBiometria } from './biometria';
import { biometriaActivada, pedirActivarAlEntrar, rechazarBiometria } from './preferenciaBiometria';
import { conTipo } from './textosBiometria';

/**
 * «Entrar con Face ID», en Configuración → Seguridad. Se quita y se pone en cualquier momento.
 *
 * - Apagarlo borra al momento el NIF y la contraseña de la caja fuerte del móvil.
 * - Encenderlo necesita la contraseña, que no tenemos (no se guarda en ningún sitio): se cierra
 *   sesión y, al entrar con la contraseña, se activa solo (opción A, decidida el 15-oct-2026).
 *
 * Solo aparece dentro de la app y si el móvil tiene biometría: en el navegador no pinta nada.
 */
const InterruptorBiometria: React.FC = () => {
	const { translate } = useContext(I18nContext).state;
	const id = useId();
	const [tipo, setTipo] = useState<TipoDeBiometria | null>(null);
	const [activada, setActivada] = useState(biometriaActivada);
	const [explicando, setExplicando] = useState(false);

	useEffect(() => {
		void tipoDeBiometria().then(setTipo);
	}, []);

	const cambiar = async (quiereActivarla: boolean) => {
		if (quiereActivarla) {
			setExplicando(true);
			return;
		}

		setActivada(false);
		rechazarBiometria();
		await borrarAcceso();
	};

	const cerrarSesionYActivar = () => {
		pedirActivarAlEntrar();
		clearContextAndLocalStorage();
		window.location.assign('/');
	};

	if (!tipo) {
		return null;
	}

	return (
		<>
			<div className="flex items-center justify-between gap-4">
				<div>
					<p id={id} className="text-sm font-medium">
						{conTipo(translate, 'movil.biometria.ajustes.titulo', tipo)}
					</p>
					<p className="text-xs text-muted-foreground">
						{conTipo(translate, 'movil.biometria.ajustes.texto', tipo)}
					</p>
				</div>
				<Switch
					aria-labelledby={id}
					checked={activada}
					onCheckedChange={(valor) => void cambiar(valor)}
				/>
			</div>
			<AlertDialog open={explicando}>
				{/* El botón atrás de Android cierra los diálogos con «Escape» (`botonAtras.ts`): vale «Cancelar». */}
				<AlertDialogContent onEscapeKeyDown={() => setExplicando(false)}>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{translate('movil.biometria.ajustes.activarTitulo')}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{translate('movil.biometria.ajustes.activarTexto')}
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel onClick={() => setExplicando(false)}>
							{translate('movil.biometria.ajustes.cancelar')}
						</AlertDialogCancel>
						<AlertDialogAction onClick={cerrarSesionYActivar}>
							{translate('movil.biometria.ajustes.activarBoton')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	);
};

export default InterruptorBiometria;
